import { useState, useRef, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Typography from '@mui/material/Typography';
import Tooltip from '@mui/material/Tooltip';
import ConfirmationNumberIcon from '@mui/icons-material/ConfirmationNumber';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import InventoryIcon from '@mui/icons-material/Inventory';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import BusinessIcon from '@mui/icons-material/Business';

import { useAuth } from '../../auth';
import '../../styles/design-tokens.css';

const SIDEBAR_WIDTH = 260;
const SIDEBAR_COLLAPSED = 72;

const portalNavItems = [
  { id: 'tickets',       label: 'My Tickets',       icon: ConfirmationNumberIcon, path: '/portal/tickets'       },
  { id: 'dispatch',      label: 'My Shipments',      icon: LocalShippingIcon,      path: '/portal/dispatch'      },
  { id: 'inventory',     label: 'On-Site Inventory', icon: InventoryIcon,          path: '/portal/inventory'     },
  { id: 'locations',     label: 'Stock Locations',   icon: LocationOnIcon,         path: '/portal/locations'     },
];

export default function PortalSidebar({ collapsed }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { currentUser } = useAuth();

  const [isScrolling, setIsScrolling] = useState(false);
  const scrollTimerRef = useRef(null);

  useEffect(() => () => clearTimeout(scrollTimerRef.current), []);

  const handleScroll = () => {
    setIsScrolling(true);
    clearTimeout(scrollTimerRef.current);
    scrollTimerRef.current = setTimeout(() => setIsScrolling(false), 800);
  };

  const isActive = (path) =>
    location.pathname === path || location.pathname.startsWith(path + '/');

  const sidebarWidth = collapsed ? SIDEBAR_COLLAPSED : SIDEBAR_WIDTH;

  return (
    <Box
      component="nav"
      sx={{ position: 'fixed',
        top: 0,
        left: 0,
        height: '100vh',
        width: sidebarWidth,
        backgroundColor: 'var(--paper)',
        display: 'flex',
        flexDirection: 'column',
        zIndex: 1200,
        transition: 'width 0.2s ease',
        overflow: 'hidden',
      }}
    >
      {/* Spacer below fixed TopBar */}
      <Box sx={{ height: 80, flexShrink: 0 }} />

      {/* Navigation */}
      <Box
        onScroll={handleScroll}
        sx={{ flex: 1,
          overflowY: 'auto',
          overflowX: 'hidden',
          py: 1,
          px: '5px',
          '&::-webkit-scrollbar': { width: '4px' },
          '&::-webkit-scrollbar-track': { background: 'transparent' },
          '&::-webkit-scrollbar-thumb': {
            borderRadius: '4px',
            background: isScrolling ? '#b0bec5' : 'transparent',
            transition: 'background 0.4s ease',
          },
          scrollbarWidth: 'thin',
          scrollbarColor: isScrolling ? '#b0bec5 transparent' : 'transparent transparent',
        }}
      >
        <List dense disablePadding>
          {portalNavItems.map((item) => {
            const Icon = item.icon;
            const active = isActive(item.path);

            return (
              <Tooltip
                key={item.id}
                title={collapsed ? item.label : ''}
                placement="right"
                disableHoverListener={!collapsed}
              >
                <ListItemButton
                  onClick={() => navigate(item.path)}
                  selected={active}
                  sx={{ ...(collapsed ? {
                      width: 46,
                      height: 46,
                      minHeight: 46,
                      p: 0,
                      mx: 'auto',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    } : {
                      minHeight: 35,
                      pt: '8px',
                      pb: '8px',
                      pl: '24px',
                      pr: '16px',
                      mx: 1,
                    }),
                    borderRadius: '6px',
                    mb: '4px',
                    backgroundColor: active ? 'var(--secondary-light)' : 'transparent',
                    '&:hover': {
                      backgroundColor: 'var(--secondary-light)',
                      '& .nav-label': { color: 'var(--secondary-main)' },
                    },
                    '&.Mui-selected': { backgroundColor: 'var(--secondary-light)' },
                    '&.Mui-selected:hover': { backgroundColor: 'var(--secondary-light)' },
                  }}
                >
                  <ListItemIcon
                    sx={{ minWidth: collapsed ? 0 : 32,
                      color: active ? 'var(--secondary-main)' : '#364152',
                      mr: collapsed ? 0 : '4px',
                    }}
                  >
                    <Icon sx={{ fontSize: 20 }} />
                  </ListItemIcon>

                  {!collapsed && (
                    <ListItemText
                      primary={item.label}
                      primaryTypographyProps={{
                        fontSize: '0.8125rem',
                        fontWeight: 500,
                        color: active ? 'var(--secondary-main)' : '#364152',
                        className: 'nav-label',
                        noWrap: true,
                      }}
                    />
                  )}
                </ListItemButton>
              </Tooltip>
            );
          })}
        </List>
      </Box>

      {/* Bottom: Customer identity chip */}
      {currentUser.customerName && (
        <Box sx={{ flexShrink: 0, borderTop: '1px solid var(--grey-100)', px: '5px', py: 1 }}>
          <Tooltip title={collapsed ? `Customer Portal — ${currentUser.customerName}` : ''} placement="right" disableHoverListener={!collapsed}>
            <Box
              sx={{ borderRadius: '6px',
                backgroundColor: 'var(--secondary-light)',
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                ...(collapsed ? {
                  width: 46,
                  height: 46,
                  mx: 'auto',
                  justifyContent: 'center',
                } : {
                  px: '16px',
                  py: '10px',
                  mx: 1,
                }),
              }}
            >
              <BusinessIcon sx={{ fontSize: 20, color: 'var(--secondary-main)', flexShrink: 0 }} />

              {!collapsed && (
                <Box sx={{ minWidth: 0 }}>
                  <Typography sx={{ fontSize: '0.6875rem', fontWeight: 600, color: 'var(--secondary-main)', textTransform: 'uppercase', letterSpacing: '0.4px', lineHeight: 1, opacity: 0.7 }}>
                    Customer Portal
                  </Typography>
                  <Typography sx={{ fontSize: '0.8125rem', fontWeight: 600, color: 'var(--secondary-main)', mt: '3px', lineHeight: 1.2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {currentUser.customerName}
                  </Typography>
                </Box>
              )}
            </Box>
          </Tooltip>
        </Box>
      )}
    </Box>
  );
}
